'use client';

import { isValidPhoneNumber } from 'libphonenumber-js';
import PhoneInput from '../../../atoms/PhoneInput/PhoneInput';
import { getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { PhoneField } from '../types';
import type { CountryCode as LibPhoneCountryCode } from 'libphonenumber-js';
import type { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';

export type PhoneControlProps = {
  field: PhoneField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  locale?: string;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors<FieldValues>;
};

/**
 * Form control for phone input fields with validation
 * @component
 * @param {PhoneControlProps} props - Component props
 * @returns {JSX.Element} Phone control component
 */
const PhoneControl = ({ field, theme, direction, locale, register, errors }: PhoneControlProps) => {
  const { disabled: formDisabled } = useFormContext();
  const isDisabled = field.disabled || formDisabled;
  // Apply required validation regardless of disabled state to preserve validation integrity
  const requiredValidationMessage = field.required
    ? (field.validationMessages.required ?? false)
    : false;

  return (
    <PhoneInput
      theme={theme}
      key={`form_field_${field.name}`}
      label={field.label}
      name={field.name}
      required={field.required}
      disabled={isDisabled}
      value={field.value ?? undefined}
      locale={locale}
      register={() =>
        register(field.name, {
          required: requiredValidationMessage,
          validate: (value: string) => {
            // Only skip validation if field is empty, not if disabled with invalid data
            if (!value) return true;
            try {
              const countryCode = (field?.selectedOption || 'US') as LibPhoneCountryCode;
              const isValid = isValidPhoneNumber(value, countryCode);
              return isValid || field.validationMessages.pattern || 'Invalid phone number';
            } catch {
              return field.validationMessages.pattern || 'Invalid phone number';
            }
          },
        })
      }
      validationResponse={getValidationResponse({ field: field.name, errors })}
      direction={direction}
      selectedOption={field?.selectedOption}
    />
  );
};

export default PhoneControl;
